import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
import DragHandleIcon from '@mui/icons-material/DragHandle';

import {
  Box,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Mirrors GotoLinksSettings, minus the module-gating: action switches are a pure
// show/hide preference (no 'disabled' state), so there's no currentPractice and
// switches are always enabled. 'shown' -> primary group (directly visible),
// 'hidden' -> behind a "show all actions" button at runtime (still reachable).

export type ActionsSettingsValue = Record<string, boolean>;

type Props = {
  value: ActionsSettingsValue;
  onChange: (v: ActionsSettingsValue) => void;
  order: string[];
  onOrderChange: (v: string[]) => void;
};

export const CoachSettingsWidgetActions: FunctionComponent<Props> = ({
  value,
  onChange,
  order,
  onOrderChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Resolve the items in the (draft) order, falling back to the default order
  // when the draft is empty. Tolerates drift via getOrderedActions.
  const orderedItems = HelpersClientWidgets.getOrderedActions(order, value);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t("coach.personal_settings.userdetail_widgets.settings.actions.description")}
      </Typography>
    </Grid>

    {/* Note: hidden actions don't disappear — they move behind a "show all
        actions" button on the client detail page. Explained here so coaches
        know hiding is non-destructive (e.g. password reset stays reachable). */}
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
        {t(HelpersClientWidgets.actionsNoteKey)}
      </Typography>
    </Grid>

    {/* Unified reorder + show/hide list. Drag handle reorders; the switch
        toggles whether an action is shown directly or tucked behind "show all". */}
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{padding: 1}}>
        <DragDropProvider
          onDragEnd={(event) => {
            if (event.canceled) return;
            const { source } = event.operation;
            // With useSortable, the OptimisticSortingPlugin reorders items by
            // mutating each sortable's `index` during dragover. By dragend the
            // source's new index reflects where it was dropped, so we splice it
            // from its initial index to its new index. We can't rely on
            // event.operation.target here — the plugin re-points it at source.
            if (!source || !isSortable(source)) return;
            const from = source.sortable.initialIndex;
            const to = source.sortable.index;
            if (from === to) return;

            const currentOrder = orderedItems.map((i) => i.key);
            const next = [...currentOrder];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            onOrderChange(next);
          }}
        >
          {orderedItems.map((item, index) => (
            <ActionSortableRow
              key={item.key}
              id={item.key}
              index={index}
              item={item}
              label={t(`coach.personal_settings.userdetail_widgets.settings.actions.items.${item.key}`)}
              theme={theme}
              checked={item.state === 'shown'}
              onToggle={(checked) => onChange({ ...value, [item.key]: checked })}
            />
          ))}
        </DragDropProvider>
      </Paper>
    </Grid>
  </Grid>;
}

type RowProps = {
  id: string;
  index: number;
  item: any;
  label: string;
  theme: any;
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

const ActionSortableRow: FunctionComponent<RowProps> = ({id, index, item, label, theme, checked, onToggle}) => {
  const { ref, handleRef, isDragSource, isDropTarget } = useSortable({ id, index });

  const { Icon } = item;
  // shown -> primary (green), hidden -> secondary (red, behind "show all")
  const stateColor = checked
    ? theme.palette.primary.main
    : theme.palette.secondary.main;

  return (
    <Box
      ref={ref}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        padding: 1,
        borderRadius: 0.5,
        marginTop: 0.5,
        backgroundColor: isDropTarget ? 'rgba(151,190,107,0.12)' : 'transparent',
        opacity: isDragSource ? 0.5 : 1,
        border: '1px solid',
        borderColor: isDropTarget ? theme.palette.primary.main : 'transparent',
        transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out'
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: theme.palette.text.secondary
        }}
      >
        <DragHandleIcon fontSize="small" />
      </Box>
      <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
      <Switcher
        label={label}
        value={checked}
        greying
        onChange={onToggle}
      />
    </Box>
  );
}

// Read-only status row: one icon per action, coloured by state.
// primary = shown (direct), secondary = hidden (behind "show all").
type StatusProps = {
  value: ActionsSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetActionsStatus: FunctionComponent<StatusProps> = ({
  value,
  order
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Icons follow the saved order (falling back to the default order when none
  // is persisted), matching what the widget renders.
  const orderedItems = HelpersClientWidgets.getOrderedActions(order, value);

  return <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1.5,
    marginTop: 1
  }}>
    {orderedItems.map((item) => {
      const { Icon } = item;
      const color = item.state === 'shown'
        ? theme.palette.primary.main
        : theme.palette.secondary.main;
      return <Box
        key={item.key}
        title={t(`coach.personal_settings.userdetail_widgets.settings.actions.items.${item.key}`)}
      >
        <Icon sx={{ fontSize: '1.1rem', color }} />
      </Box>;
    })}
  </Box>;
}
